import Dexie, { type Table } from 'dexie';
import type { Deck, LeagueSettings, Match, Pilot, Session } from '@edh/shared';
import { DEFAULT_SETTINGS } from '@edh/shared';

/**
 * The local mirror. Spec 3b: "a rating tracker that can't show you your decks
 * offline is broken." Everything the app renders comes from here; the network
 * only ever refreshes it or drains the queue.
 */

export interface LeagueState {
  pilots: Pilot[];
  decks: Deck[];
  matches: Match[];
  sessions: Session[];
  settings: LeagueSettings;
}

export const emptyState = (): LeagueState => ({
  pilots: [],
  decks: [],
  matches: [],
  sessions: [],
  settings: { ...DEFAULT_SETTINGS },
});

export type Mutation =
  | { kind: 'createPilot'; payload: { id: string; name: string } }
  | { kind: 'updatePilot'; payload: { id: string } & Partial<Pilot> }
  | { kind: 'createDeck'; payload: Partial<Deck> & { id: string; name: string } }
  | { kind: 'updateDeck'; payload: { id: string } & Partial<Deck> }
  | { kind: 'deletePilot'; payload: { id: string } }
  | { kind: 'deleteDeck'; payload: { id: string } }
  | { kind: 'logMatch'; payload: Record<string, unknown> & { clientId: string } }
  | { kind: 'undoLast'; payload: Record<string, never> }
  | { kind: 'deleteMatch'; payload: { matchId: string } }
  | {
      kind: 'editMatch';
      payload: {
        matchId: string;
        winnerPilotId?: string;
        endTurn?: number | null;
        winCondition?: string | null;
        note?: string | null;
      };
    }
  | { kind: 'settings'; payload: Partial<LeagueSettings> };

export interface QueuedMutation {
  seq?: number;
  mutation: Mutation;
  createdAt: number;
}

interface MetaRow {
  key: string;
  value: unknown;
}

interface ArtRow {
  deckId: string;
  blob: Blob;
  fetchedAt: number;
}

class EdhDb extends Dexie {
  meta!: Table<MetaRow, string>;
  queue!: Table<QueuedMutation, number>;
  art!: Table<ArtRow, string>;

  constructor() {
    super('edh-misadventures');
    this.version(1).stores({
      meta: 'key',
      queue: '++seq',
      art: 'deckId',
    });
  }
}

export const db = new EdhDb();

export async function readState(): Promise<LeagueState | null> {
  const row = await db.meta.get('state');
  return (row?.value as LeagueState) ?? null;
}

export async function writeState(state: LeagueState): Promise<void> {
  await db.meta.put({ key: 'state', value: state });
}

/** Mutations replay in the order they were made; the seq key guarantees it. */
export async function enqueue(mutation: Mutation): Promise<void> {
  await db.queue.add({ mutation, createdAt: Date.now() });
}

export async function pending(): Promise<QueuedMutation[]> {
  return db.queue.orderBy('seq').toArray();
}

export async function dequeue(seq: number): Promise<void> {
  await db.queue.delete(seq);
}

export async function pendingCount(): Promise<number> {
  return db.queue.count();
}

// ---- Art --------------------------------------------------------------------

export async function readArt(deckId: string): Promise<Blob | null> {
  const row = await db.art.get(deckId);
  return row?.blob ?? null;
}

export async function writeArt(deckId: string, blob: Blob): Promise<void> {
  await db.art.put({ deckId, blob, fetchedAt: Date.now() });
}

export async function hasArt(deckId: string): Promise<boolean> {
  return (await db.art.where('deckId').equals(deckId).count()) > 0;
}
